import { useEffect, useRef } from 'react';
import WRAP from 'WRAP-JS';
import ContentsListPanel from "./ContentsListPanel";
import FIR from "./layers/FIR";
import Airport from "./layers/Airport";
import LonLat from "./layers/LonLat";
import { convertWRAPPoint, formatLonLatDM } from "../../utils/FPEUtils";
import Fix from "./layers/Fix";
import ContentsLegendPanel from "./ContentsLegendPanel";
import Airway from "./layers/Airway";
import FlightPlan from "./layers/FlightPlan";
import Sigmet from "./layers/Sigmet";
import useEffectSkipFirst from "../../hooks/useEffectSkipFirst";
import ContentsInfoPanel from "./ContentsInfoPanel";
import TileMap from "./layers/TileMap";
import Vaa from "./layers/Vaa";
import Sigwx from "./layers/Sigwx";
import Ukmet from "./layers/Ukmet";
import Satellite from "./layers/Satellite";
import AscScale from "./layers/AscScale";
import Acos from "./layers/Acos";
import Radar from "./layers/Radar";
import SurfaceAnalysis from "./layers/SurfaceAnalysis";
import TropicalCyclone from "./layers/TropicalCyclone";
import MetarTaf from "./layers/MetarTaf";
import { useRecoilValue } from "recoil";
import { polarRotationState } from "../../store/PolarRotationState";
import { useMapColorState } from "../../store/MapColorState";
import { mapPerspectiveState } from "../../store/MapPerspectiveState";
import { useMapProjectionState } from "../../store/MapProjectionState";
import ContentsDispatcher from "./dispatch/ContentsDispatcher.js";
import WxContentsDispatcher from "./dispatch/WxContentsDispatcher";
import {
  WX_ID_ACOS,
  WX_ID_ASCSCALE, WX_ID_METARTAF, WX_ID_RADAR,
  WX_ID_SATELLITE,
  WX_ID_SIGMET,
  WX_ID_SIGWX, WX_ID_SURFACEANALYSIS, WX_ID_TROPICALCYCLONE,
  WX_ID_UKMET,
  WX_ID_VAA, WX_SETTINGS
} from "../../store/WeatherContentsState";
import {
  MAP_ID_AIRPORT,
  MAP_ID_AIRWAY, MAP_ID_COASTLINE,
  MAP_ID_FIR,
  MAP_ID_FIX, MAP_ID_FLIGHTPLAN,
  MAP_ID_LONLAT, MAP_ID_NAVAIDS,
  MAP_ID_TILEMAP
} from "../../store/MapContentsState";
import FlightPlanDispatcher from "./dispatch/FlightPlanDispatcher";
import Navaids from "./layers/Navaids";
import CoastLine from "./layers/CoastLine";
import { getCustomerSettings } from "../../CustomerSettings";
import ToggleContentsInfo from "./ToggleContentsInfo";
import ToggleLegend from "./ToggleLegend";
import Tooltip from "./helper/Tooltip";
import SigmetDispatcher from "./dispatch/SigmetDispatcher";
import SigmetUnreadPositionPanel from "./SigmetUnreadPositionPanel";
import { routePointEditingState } from '../../store/RoutePointEditingState';

function Map() {

  const {projection, isMercator, isPolarN, isPolarS} = useMapProjectionState();
  const {mapColor} = useMapColorState();
  const mapPerspective = useRecoilValue(mapPerspectiveState);
  const polarRotation = useRecoilValue(polarRotationState);
  const layerConfig = getCustomerSettings('layerConfig');
  const routePointEditing = useRecoilValue(routePointEditingState);

  useEffect(() => {
    initMap();
    // 初回render時のみ実行するので警告無視
    // eslint-disable-next-line
  },[]);

  const isFirstRender = useRef(true);
  if (isFirstRender.current) {
    isFirstRender.current = false;
    initMapContents(mapColor, [0,530], layerConfig);
  }

  useEffect(() => {
    setProjection(projection, isMercator, isPolarN, isPolarS);
  },[projection, isMercator, isPolarN, isPolarS]);

  useEffect(() => {
    if (!isMercator) {
      setPolarRotation(polarRotation);
    }
  }, [polarRotation, isMercator]);

  useEffect(() => {
    adjustCenterPosition(mapPerspective.centerLat, mapPerspective.centerLon, mapPerspective.zoom, isPolarN, isPolarS);
  }, [mapPerspective, isPolarN, isPolarS]);

  useEffectSkipFirst(() => {
    applyMapColor(mapColor);
  }, mapColor);

  useEffect(() => {
    changeRoutePointEditing(routePointEditing);
  }, [routePointEditing]);

  return (
    <>
      <div id="map" className={'map ' + mapColor}/>
      <ContentsListPanel/>
      <ContentsLegendPanel/>
      <ContentsInfoPanel/>
      <ToggleContentsInfo/>
      <ToggleLegend/>
      <div id="mouse-point"/>
      <div id="map-tooltip" className="map-tooltip"/>
      <SigmetUnreadPositionPanel/>
      <FlightPlanDispatcher contents={mapContents[MAP_ID_FLIGHTPLAN]}/>
      {mapContents[WX_ID_SIGMET] && <SigmetDispatcher contentsID={WX_ID_SIGMET} contents={mapContents[WX_ID_SIGMET]} altType={WX_SETTINGS[WX_ID_SIGMET].altitude}/>}
      {mapContents[WX_ID_VAA] &&<WxContentsDispatcher contentsID={WX_ID_VAA} contents={mapContents[WX_ID_VAA]} altType={WX_SETTINGS[WX_ID_VAA].altitude}/>}
      {mapContents[WX_ID_SIGWX] && <WxContentsDispatcher contentsID={WX_ID_SIGWX} contents={mapContents[WX_ID_SIGWX]} altType={WX_SETTINGS[WX_ID_SIGWX].altitude}/>}
      {mapContents[WX_ID_UKMET] && <WxContentsDispatcher contentsID={WX_ID_UKMET} contents={mapContents[WX_ID_UKMET]} altType={WX_SETTINGS[WX_ID_UKMET].altitude}/>}
      {mapContents[WX_ID_SATELLITE] && <WxContentsDispatcher contentsID={WX_ID_SATELLITE} contents={mapContents[WX_ID_SATELLITE]} altType={WX_SETTINGS[WX_ID_SATELLITE].altitude}/>}
      {mapContents[WX_ID_ASCSCALE] && <WxContentsDispatcher contentsID={WX_ID_ASCSCALE} contents={mapContents[WX_ID_ASCSCALE]} altType={WX_SETTINGS[WX_ID_ASCSCALE].altitude}/>}
      {mapContents[WX_ID_ACOS] && <WxContentsDispatcher contentsID={WX_ID_ACOS} contents={mapContents[WX_ID_ACOS]} altType={WX_SETTINGS[WX_ID_ACOS].altitude}/>}
      {mapContents[WX_ID_RADAR] && <WxContentsDispatcher contentsID={WX_ID_RADAR} contents={mapContents[WX_ID_RADAR]} altType={WX_SETTINGS[WX_ID_RADAR].altitude}/>}
      {mapContents[WX_ID_SURFACEANALYSIS] && <WxContentsDispatcher contentsID={WX_ID_SURFACEANALYSIS} contents={mapContents[WX_ID_SURFACEANALYSIS]} altType={WX_SETTINGS[WX_ID_SURFACEANALYSIS].altitude}/>}
      {mapContents[WX_ID_TROPICALCYCLONE] && <WxContentsDispatcher contentsID={WX_ID_TROPICALCYCLONE} contents={mapContents[WX_ID_TROPICALCYCLONE]} altType={WX_SETTINGS[WX_ID_TROPICALCYCLONE].altitude}/>}
      {mapContents[WX_ID_METARTAF] && <WxContentsDispatcher contentsID={WX_ID_METARTAF} contents={mapContents[WX_ID_METARTAF]} altType={WX_SETTINGS[WX_ID_METARTAF].altitude}/>}
      {mapContents[MAP_ID_TILEMAP] && <ContentsDispatcher contentsID={MAP_ID_TILEMAP} contents={mapContents[MAP_ID_TILEMAP]}/>}
      {mapContents[MAP_ID_LONLAT] && <ContentsDispatcher contentsID={MAP_ID_LONLAT} contents={mapContents[MAP_ID_LONLAT]}/>}
      {mapContents[MAP_ID_FIR] && <ContentsDispatcher contentsID={MAP_ID_FIR} contents={mapContents[MAP_ID_FIR]}/>}
      {mapContents[MAP_ID_AIRPORT] && <ContentsDispatcher contentsID={MAP_ID_AIRPORT} contents={mapContents[MAP_ID_AIRPORT]}/>}
      {mapContents[MAP_ID_FIX] && <ContentsDispatcher contentsID={MAP_ID_FIX} contents={mapContents[MAP_ID_FIX]}/>}
      {mapContents[MAP_ID_AIRWAY] && <ContentsDispatcher contentsID={MAP_ID_AIRWAY} contents={mapContents[MAP_ID_AIRWAY]}/>}
      {mapContents[MAP_ID_NAVAIDS] && <ContentsDispatcher contentsID={MAP_ID_NAVAIDS} contents={mapContents[MAP_ID_NAVAIDS]}/>}
      {mapContents[MAP_ID_COASTLINE] && <ContentsDispatcher contentsID={MAP_ID_COASTLINE} contents={mapContents[MAP_ID_COASTLINE]}/>}
    </>
  );
}

export default Map;

const initMap = () => {
  const mapDiv = document.getElementById('map');
  WRAP.Geo.setCanvasMap(mapDiv, {
    min_zoom: getZoomRange().min_zoom,
    max_zoom: 9
  });
  WRAP.Geo.setInteraction(mapDiv);
  initLayer();
  initInteraction(mapDiv);

}

let mapContents = {};

const initMapContents = (mapColor, altitude, layerConfig) => {

  mapContents[MAP_ID_FLIGHTPLAN] = new FlightPlan(mapColor);

  const factory = (id, clz, ...arg) => {
    if (layerConfig[id]) {
      mapContents[id] = new clz(...arg);
    }
  }

  factory(MAP_ID_TILEMAP, TileMap, mapColor);
  factory(MAP_ID_COASTLINE, CoastLine, mapColor);
  factory(MAP_ID_LONLAT, LonLat);
  factory(MAP_ID_FIR, FIR);
  factory(MAP_ID_AIRPORT, Airport);
  factory(MAP_ID_AIRWAY, Airway);
  factory(MAP_ID_FIX, Fix);
  factory(MAP_ID_NAVAIDS, Navaids);
  factory(WX_ID_SIGMET, Sigmet, altitude);
  factory(WX_ID_VAA, Vaa, altitude);
  factory(WX_ID_SIGWX, Sigwx, altitude);
  factory(WX_ID_UKMET, Ukmet);
  factory(WX_ID_SATELLITE, Satellite);
  factory(WX_ID_ASCSCALE, AscScale);
  factory(WX_ID_ACOS, Acos, mapColor);
  factory(WX_ID_RADAR, Radar);
  factory(WX_ID_SURFACEANALYSIS, SurfaceAnalysis);
  factory(WX_ID_TROPICALCYCLONE, TropicalCyclone);
  factory(WX_ID_METARTAF, MetarTaf);
};

const initLayer = () => {
  const layerOrder = getCustomerSettings('layerOrder');

  let contentsLayers = layerOrder.reduce((list, contentsId) => {
    if (mapContents[contentsId]) {
      list.push(mapContents[contentsId].getLayer());
    }
    return list;
  },[]);

  contentsLayers.push(mapContents[MAP_ID_FLIGHTPLAN].getLayer());

  const maskLayers = getMaskLayers();
  const layers = contentsLayers.flat().concat(maskLayers).reverse();
  WRAP.Geo.setLayer(layers);
}

const initInteraction = (mapDiv) => {
  for(const key in mapContents) {
    const contents = mapContents[key];
    if(typeof contents.initInteraction === 'function') {
      contents.initInteraction();
    }
  }
  mapDiv.addEventListener('mousemove', displayMousePoint);
  WRAP.Geo.addEventHandler('boundsChange', updateBounds);
  WRAP.Geo.addEventHandler('mapDragStart', mapDragStart);
  WRAP.Geo.addEventHandler('mapDragEnd', mapDragEnd);
  window.addEventListener('resize', windowResize);

  const tooltip = new Tooltip(mapDiv, 'map-tooltip');
  tooltip.addLayer(mapContents[MAP_ID_FIR]);
};

const getMaskLayers = () => {
  const northMaskLayer = new WRAP.Geo.Layer('North_Mask');
  const southMaskLayer = new WRAP.Geo.Layer('South_Mask');

  let north_polygon = [];
  for (let lon = 0; lon < 360; lon += 10) {
    let points = [];
    points.push([lon, 0]);
    points.push([lon + 10, 0]);
    points.push([lon + 10, -80]);
    points.push([lon, -80]);
    points.push([lon, 0]);
    north_polygon.push(points);
  }
  northMaskLayer.addFeature(new WRAP.Geo.Feature.GeoLine({
    path: north_polygon,
    fillStyle: 'rgba(128,160,180,0.95)'
  }));
  northMaskLayer.hide();

  let south_polygon = [];
  for (let lon = 0; lon < 360; lon += 10) {
    let points = [];
    points.push([lon, 0]);
    points.push([lon + 10, 0]);
    points.push([lon + 10, 80]);
    points.push([lon, 80]);
    points.push([lon, 0]);
    south_polygon.push(points);
  }
  southMaskLayer.addFeature(new WRAP.Geo.Feature.GeoLine({
    path: south_polygon,
    fillStyle: 'rgba(128,160,180,0.95)'
  }));
  southMaskLayer.hide();

  return [northMaskLayer, southMaskLayer];
};

const setProjection = (projection, isMercator, isPolarN, isPolarS) => {
  const nMask = WRAP.Geo.view.layers.find(l => l._name ==="North_Mask");
  const sMask = WRAP.Geo.view.layers.find(l => l._name ==="South_Mask");
  nMask.hide();
  sMask.hide();
  if (isMercator) {
    if (WRAP.Geo.view.context.projection_name === 'Mercator') {
      return;
    }
    WRAP.Geo.setView('Mercator');
  } else if (isPolarN) {
    WRAP.Geo.setView('PolarStereographic', { base_lon: 140, base_lat: 90, lat_ts: 45 });
    nMask.show();
  } else if (isPolarS) {
    WRAP.Geo.setView('PolarStereographic', { base_lon: 140, base_lat: -90, lat_ts: -45 });
    sMask.show();
  }
  for(const key in mapContents) {
    const contents = mapContents[key];
    if(typeof contents.changeProjection === 'function') {
      contents.changeProjection();
    }
  }
};

const setPolarRotation = (rotation) =>{
  const opt = WRAP.Geo.currentView().projection.opt;

  if (opt.base_lon === rotation) {
    return;
  }
  WRAP.Geo.setView('PolarStereographic', {...opt, base_lon:rotation});
}

const adjustCenterPosition = (lat, lon, zoom, isPolarN, isPolarS) => {
  const center = convertWRAPPoint(lat, lon);
  if ((isPolarN && center.lat < 0) || (isPolarS && center.lat > 0)) {
    center.lat = -center.lat;
  }
  if (zoom < WRAP.Geo.currentView().min_zoom) {
    WRAP.Geo.setZoomRange(zoom, WRAP.Geo.currentView().max_zoom);
  }
  WRAP.Geo.setCenterPoint(center);
  WRAP.Geo.setZoom(zoom)
};

const displayMousePoint = event => {
  const rect = event.currentTarget.getBoundingClientRect();
  const pt = WRAP.Geo.getPoint(new WRAP.Geo.ScreenPoint(event.clientX - rect.left, event.clientY - rect.top));
  document.getElementById('mouse-point').innerText = formatLonLatDM(pt.lonDegree(), pt.latDegree());
};

/**
 * 現在の画面サイズに合わせて地図のズームレベル範囲を取得する
 * @returns {{min_zoom: number, max_zoom: number}}
 */
const getZoomRange = () => {
  const width = document.getElementById('map').clientWidth;
  const min = Math.LOG2E * Math.log(width / 190);
  const max = min+6;
  return {
    min_zoom: min,
    max_zoom: max
  }
}

let prevZoom;
const changeZoomLevel = () => {
  const zoom = getZoomRange();
  if (!prevZoom || (prevZoom.min_zoom !== zoom.min_zoom && prevZoom.max_zoom !== zoom.max_zoom)) {
    WRAP.Geo.setZoomRange(zoom.min_zoom, zoom.max_zoom);
    prevZoom = zoom;
  }
}

let resizeTimer;
const windowResize = () => {
  if (resizeTimer) {
    clearTimeout(resizeTimer);
  }
  resizeTimer = setTimeout(function () {
    if (!WRAP.Geo.view.map || !WRAP.Geo.view.map.ready()) {
      return;
    }
    changeZoomLevel();
  }, 300);
};

let updateBoundsTimer;
const updateBounds = () => {
  if (updateBoundsTimer) {
    clearTimeout(updateBoundsTimer);
  }
  updateBoundsTimer = setTimeout(() => {
    if (!WRAP.Geo.view.map.ready()) {
      return;
    }
    changeZoomLevel();
    for (let mapContentsKey in mapContents) {
      mapContents[mapContentsKey].updateBounds && mapContents[mapContentsKey].updateBounds();
    }
  }, 300);
};

const applyMapColor = mapColor => {
  for (let mapContentsKey in mapContents) {
    mapContents[mapContentsKey].applyMapColor && mapContents[mapContentsKey].applyMapColor(mapColor);
  }
};

const mapDragStart = () => {
  document.getElementById('map-wrap').style.userSelect = 'none';
}

const mapDragEnd = () => {
  document.getElementById('map-wrap').style.userSelect = 'auto';
}

const changeRoutePointEditing = routePointEditing => {
  for (let mapContentsKey in mapContents) {
    mapContents[mapContentsKey].changeRoutePointEditing && mapContents[mapContentsKey].changeRoutePointEditing(routePointEditing);
  }
}
